/**
 * GlobalSearchPanel — renders one branch of the search result by `kind`.
 *
 * Navigation:
 *   - order row       → /orders/:id
 *   - product row     → /products/:id
 *   - product "see more" → /products?search=<q>
 *   - customer card + "see all orders" → /orders?search=<phone>
 *   - inline order (on customer card)  → /orders/:id
 */

import { Link } from "react-router-dom";
import { imageUrl } from "@a2z/ui/media/image-url";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { getSellerPath } from "@/lib/routing/portal-urls";
import { MEDIA_BASE_URL } from "@/lib/media-base";
import { ImageThumb } from "@/shared/components/PlaceholderImage";

/**
 * Order results open the orders list with the detail modal (the same way the
 * list itself opens an order), via ?order=<id> — not the standalone detail
 * page. Keeps the seller in context; closing the modal returns to the list.
 */
function orderModalHref(locale: string, storeSlug: string, orderId: string) {
  return `${getSellerPath(locale, storeSlug, "/orders")}?order=${orderId}`;
}
import type {
  SellerSearchResponse,
  OrderSearchHit,
  CustomerSearchHit,
  ProductSearchHit,
  OrderSearchHitLite,
} from "@/lib/api/types";
import { useCustomerLockCopy } from "@/shared/hooks/useCustomerLockCopy";
import { formatCurrency } from "@/lib/formatting/currency";
import { activeFormattingLocale } from "@/lib/formatting/locale";

/** Money in the portal's language ("٣٥٠ ج.م"), not "350 EGP" (B11). */
function formatEgpAmount(amount: number): string {
  return formatCurrency(amount, activeFormattingLocale() === "ar" ? "ar" : "en");
}

// ─── Props ────────────────────────────────────────────────────────────────────

type Props = {
  result: SellerSearchResponse;
  locale: string;
  storeSlug: string;
  /** Raw query — used for "see more products" link */
  q: string;
  onNavigate?: () => void;
  /** DOM id of the currently keyboard-highlighted row (from GlobalSearch) */
  activeRowId?: string;
  /** Shared panel id prefix used to build stable row element ids */
  panelId?: string;
};

// ─── Main panel ───────────────────────────────────────────────────────────────

export function GlobalSearchPanel({
  result,
  locale,
  storeSlug,
  q,
  onNavigate,
  activeRowId,
  panelId = "",
}: Props) {
  const t = useTranslations();

  if (result.kind === "none") {
    return (
      <div
        role="status"
        className="px-4 py-6 text-center text-[13px] text-[color:var(--text-muted)]"
      >
        {t(keys.globalSearch.empty)}
      </div>
    );
  }

  if (result.kind === "orders") {
    // Empty branch: API classified query as orders but returned 0 hits
    if (result.orders.length === 0) {
      return (
        <div
          role="status"
          className="px-4 py-6 text-center text-[13px] text-[color:var(--text-muted)]"
        >
          {t(keys.globalSearch.empty)}
        </div>
      );
    }
    return (
      <div>
        <SectionHeader>{t(keys.globalSearch.sectionOrders)}</SectionHeader>
        {result.orders.map((order) => (
          <OrderRow
            key={order.id}
            id={`${panelId}-row-order-${order.id}`}
            order={order}
            href={orderModalHref(locale, storeSlug, order.id)}
            onNavigate={onNavigate}
            isActive={`${panelId}-row-order-${order.id}` === activeRowId}
          />
        ))}
      </div>
    );
  }

  if (result.kind === "customers") {
    // Empty branch: phone pattern matched but no customers found
    if (result.customers.length === 0) {
      return (
        <div
          role="status"
          className="px-4 py-6 text-center text-[13px] text-[color:var(--text-muted)]"
        >
          {t(keys.globalSearch.empty)}
        </div>
      );
    }
    return (
      <div>
        <SectionHeader>{t(keys.globalSearch.sectionCustomers)}</SectionHeader>
        {result.customers.map((customer) => (
          <CustomerCard
            key={customer.id}
            customer={customer}
            locale={locale}
            storeSlug={storeSlug}
            onNavigate={onNavigate}
            activeRowId={activeRowId}
            panelId={panelId}
          />
        ))}
      </div>
    );
  }

  if (result.kind === "mixed") {
    const hasCustomers = result.customers.length > 0;
    const hasProducts = result.products.length > 0;

    // Empty branch: free-text query matched neither customers nor products
    if (!hasCustomers && !hasProducts) {
      return (
        <div
          role="status"
          className="px-4 py-6 text-center text-[13px] text-[color:var(--text-muted)]"
        >
          {t(keys.globalSearch.empty)}
        </div>
      );
    }

    const seeMoreHref = (() => {
      const params = new URLSearchParams({ search: q });
      return `${getSellerPath(locale, storeSlug, "/products")}?${params.toString()}`;
    })();

    return (
      <div>
        {hasCustomers ? (
          <div>
            <SectionHeader>{t(keys.globalSearch.sectionCustomers)}</SectionHeader>
            {result.customers.map((customer) => (
              <CustomerCard
                key={customer.id}
                customer={customer}
                locale={locale}
                storeSlug={storeSlug}
                onNavigate={onNavigate}
                activeRowId={activeRowId}
                panelId={panelId}
              />
            ))}
          </div>
        ) : null}

        {hasProducts ? (
          <div
            className={
              hasCustomers
                ? "border-t border-[color:var(--border-light)] pt-1"
                : undefined
            }
          >
            <SectionHeader>{t(keys.globalSearch.sectionProducts)}</SectionHeader>
            {result.products.map((product) => (
              <ProductRow
                key={product.id}
                id={`${panelId}-row-product-${product.id}`}
                product={product}
                locale={locale}
                href={getSellerPath(locale, storeSlug, `/products/${product.id}`)}
                onNavigate={onNavigate}
                isActive={`${panelId}-row-product-${product.id}` === activeRowId}
              />
            ))}
            <div className="mt-1 border-t border-[color:var(--border-light)] px-4 py-2.5">
              <SeeMoreLink href={seeMoreHref} onNavigate={onNavigate}>
                {t(keys.globalSearch.seeMoreProducts)}
              </SeeMoreLink>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return null;
}

// ─── Section header ───────────────────────────────────────────────────────────

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-[color:var(--text-muted)]">
      {children}
    </div>
  );
}

// ─── Order row ────────────────────────────────────────────────────────────────

function OrderRow({
  id,
  order,
  href,
  onNavigate,
  isActive = false,
}: {
  id?: string;
  order: OrderSearchHit | OrderSearchHitLite;
  href: string;
  onNavigate?: () => void;
  isActive?: boolean;
}) {
  const lockCopy = useCustomerLockCopy();
  const locked = order.customerInfoLocked ?? false;
  // A held order arrives with a zeroed total; printing it would read as a
  // free order rather than a withheld one.
  const customerName = "customerName" in order ? order.customerName : null;
  return (
    <Link
      id={id}
      to={href}
      role="option"
      aria-selected={isActive}
      onClick={onNavigate}
      className={[
        "flex items-center gap-3 px-4 py-2.5 transition-colors",
        isActive
          ? "bg-[color:var(--background-muted)]"
          : "hover:bg-[color:var(--background-muted)]",
      ].join(" ")}
    >
      <OrderIcon />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium text-[color:var(--text-primary)]">
          <bdi dir="ltr">#{order.orderCode}</bdi>
        </p>
        {locked ? (
          <p className="truncate text-[12px] italic text-[color:var(--text-muted)]">
            {lockCopy.label}
          </p>
        ) : customerName ? (
          <p className="truncate text-[12px] text-[color:var(--text-muted)]">
            {customerName}
          </p>
        ) : null}
      </div>
      <span className="shrink-0 text-[12px] text-[color:var(--text-muted)]">
        {locked ? lockCopy.amountLabel : `${order.totalAmount} EGP`}
      </span>
    </Link>
  );
}

// ─── Customer card ────────────────────────────────────────────────────────────

function CustomerCard({
  customer,
  locale,
  storeSlug,
  onNavigate,
  activeRowId,
  panelId = "",
}: {
  customer: CustomerSearchHit;
  locale: string;
  storeSlug: string;
  onNavigate?: () => void;
  activeRowId?: string;
  panelId?: string;
}) {
  const t = useTranslations();

  // Both the card click and "see all orders" navigate to /orders?search=<phone>
  // NOTE: match relies on the backend orders-list free-text handler doing a substring `contains` on phoneE164 (leading `+` is tolerated).
  const allOrdersHref = (() => {
    const params = new URLSearchParams({ search: customer.phoneE164 });
    return `${getSellerPath(locale, storeSlug, "/orders")}?${params.toString()}`;
  })();

  const customerRowId = `${panelId}-row-customer-${customer.id}`;
  const isCustomerActive = customerRowId === activeRowId;

  return (
    <div className="px-4 py-2.5">
      {/* Card header — click → all orders for this customer */}
      <Link
        id={customerRowId}
        to={allOrdersHref}
        role="option"
        aria-selected={isCustomerActive}
        onClick={onNavigate}
        className={[
          "-mx-2 flex items-center gap-3 rounded-lg px-2 py-1 transition-colors",
          isCustomerActive
            ? "bg-[color:var(--background-muted)]"
            : "hover:bg-[color:var(--background-muted)]",
        ].join(" ")}
      >
        <CustomerIcon />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-medium text-[color:var(--text-primary)]">
            {customer.name}
          </p>
          <p className="truncate text-[12px] text-[color:var(--text-muted)]">
            <bdi dir="ltr">{customer.phoneE164}</bdi>
          </p>
        </div>
      </Link>

      {/* Recent orders + "see all", grouped under the customer via a vertical
          connector line that drops from beneath the avatar — so the orders
          read as clearly belonging to the customer above, not as siblings.
          ms-2 aligns the line under the avatar's center; ps-5 aligns the
          content with the customer's name. */}
      <div className="ms-2 mt-1 flex flex-col gap-0.5 border-s border-[color:var(--border-light)] ps-3">
        {customer.recentOrders.map((order) => {
          const orderRowId = `${panelId}-row-order-${order.id}`;
          const isOrderActive = orderRowId === activeRowId;
          return (
            <Link
              id={orderRowId}
              key={order.id}
              to={orderModalHref(locale, storeSlug, order.id)}
              role="option"
              aria-selected={isOrderActive}
              onClick={onNavigate}
              className={[
                "flex items-center justify-between rounded-md px-2 py-1 text-[12px] transition-colors",
                isOrderActive
                  ? "bg-[color:var(--background-muted)]"
                  : "hover:bg-[color:var(--background-muted)]",
              ].join(" ")}
            >
              <span className="truncate font-medium text-[color:var(--text-secondary)]">
                <bdi dir="ltr">#{order.orderCode}</bdi>
              </span>
              <span className="ms-2 shrink-0 text-[color:var(--text-muted)]">
                <bdi>{formatEgpAmount(Number(order.totalAmount))}</bdi>
              </span>
            </Link>
          );
        })}

        {/* "See all orders" sits inside the same connector column, aligned
            with the order rows above it. */}
        <div className="mt-0.5 px-2">
          <SeeMoreLink href={allOrdersHref} onNavigate={onNavigate}>
            {t(keys.globalSearch.seeAllOrders)}
          </SeeMoreLink>
        </div>
      </div>
    </div>
  );
}

// ─── Product row ──────────────────────────────────────────────────────────────

function ProductRow({
  id,
  product,
  locale,
  href,
  onNavigate,
  isActive = false,
}: {
  id?: string;
  product: ProductSearchHit;
  locale: string;
  href: string;
  onNavigate?: () => void;
  isActive?: boolean;
}) {
  // Show a single, locale-appropriate name (nameAr is always present;
  // nameEn is optional). Avoids the cluttered "Arabic · English" mix.
  const name =
    locale === "ar"
      ? product.nameAr
      : product.nameEn || product.nameAr;

  const thumbSrc = product.imageKey
    ? imageUrl(
        product.imageKey,
        { width: 80, format: "auto" },
        { baseUrl: MEDIA_BASE_URL },
      )
    : null;

  return (
    <Link
      id={id}
      to={href}
      role="option"
      aria-selected={isActive}
      onClick={onNavigate}
      className={[
        "flex items-center gap-3 px-4 py-2.5 transition-colors",
        isActive
          ? "bg-[color:var(--background-muted)]"
          : "hover:bg-[color:var(--background-muted)]",
      ].join(" ")}
    >
      <ImageThumb
        src={thumbSrc}
        alt={name}
        seed={product.slug || product.id}
        size="h-9 w-9 shrink-0 rounded-lg"
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium text-[color:var(--text-primary)]">
          {name}
        </p>
      </div>
      <span className="shrink-0 text-[12px] text-[color:var(--text-muted)]">
        {product.price} EGP
      </span>
    </Link>
  );
}

// ─── "See more / See all" link ─────────────────────────────────────────────────

function SeeMoreLink({
  href,
  children,
  onNavigate,
}: {
  href: string;
  children: React.ReactNode;
  onNavigate?: () => void;
}) {
  return (
    <Link
      to={href}
      onClick={onNavigate}
      className="inline-flex items-center gap-1 text-[12px] font-medium text-[color:var(--accent-600)] transition-colors hover:text-[color:var(--accent-500)]"
    >
      {children}
      <ChevronEndSmallIcon />
    </Link>
  );
}

// ─── Inline icons ─────────────────────────────────────────────────────────────

function OrderIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0 text-[color:var(--text-muted)]"
    >
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  );
}

function CustomerIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0 text-[color:var(--text-muted)]"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function ChevronEndSmallIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0 rtl:rotate-180"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
